/**
 * Create-question modal: authors a new Question Bank question of the block's
 * type against a LOCAL draft block (never the store — draft isolation). The
 * editable fields and client validation come from the shared QuestionForm /
 * useQuestionDraft primitives; this modal owns the centered shell, the
 * "added to this lesson block" framing, and the create mutation. Saving is
 * explicit: form.validate() (client) → useCreateQuestionMutation (server
 * enforces the same schema). Failures keep the modal open with the entered
 * values; backdrop clicks never close it.
 */
import { useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import type {
  QuestionBlockType,
  QuestionDefinitionDto,
  PublishJobSummary,
} from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';

import {
  QUESTION_SAVE_ERROR_MESSAGE,
  narrowQuestionApiIssues,
  useCreateQuestionMutation,
  type QuestionApiIssue,
} from '../../api/questionApi';
import { toScopeAccountId, useClientContext } from '../../hooks/useClientContext';
import { getAccountLabel } from '../layout/ClientContextPicker';
import { createBlockFromType } from '../../utils/blockFactory';
import { EMPTY_RICH_TEXT_DOC } from '../interactionEditorUtils';
import { ModalShell } from '../ModalShell';
import { ModalFooter } from '../ModalFooter';
import {
  isQuestionBlock,
  questionBlockTypeLabel,
  type QuestionBlock,
} from './questionBlockConversion';
import { QuestionForm, useQuestionDraft } from './QuestionForm';


export interface CreateQuestionModalProps {
  blockType: QuestionBlockType;
  /** Initial Single Choice mode; the toggle stays switchable inside the modal. */
  gated: boolean;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (dto: QuestionDefinitionDto & { publishJob?: PublishJobSummary | null }) => void;
  initialQuestionText?: string;
  /**
   * NA-384: the lesson block's own title text, if the author already typed
   * one into the block's heading before opening this modal — carried over
   * as the new bank question's starting Title rather than discarding it.
   */
  initialTitle?: string;
}

function buildDraftBlock(
  blockType: QuestionBlockType,
  gated: boolean,
  initialQuestionText?: string,
  initialTitle?: string,
): QuestionBlock {
  const created = createBlockFromType(blockType);
  if (!isQuestionBlock(created)) {
    throw new Error(`Unexpected non-question block type: ${blockType}`);
  }
  const description: JSONContent = initialQuestionText && initialQuestionText.trim() !== ''
    ? {
      type: 'doc',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: initialQuestionText }] }],
    }
    : EMPTY_RICH_TEXT_DOC;
  const draft: QuestionBlock = {
    ...created,
    id: 'question-modal-draft',
    title: initialTitle ?? '',
    description,
  };
  if (draft.type === 'single-choice-answer' && !gated) {
    return {
      ...draft,
      gated: false,
      correctOptionId: undefined,
      correctExplanation: undefined,
    };
  }
  return draft;
}

function toDisplayError(error: unknown): { message: string; issues: QuestionApiIssue[] } {
  if (typeof error === 'object' && error !== null) {
    const record = error as { message?: unknown; issues?: unknown };
    const message = typeof record.message === 'string' ? record.message : QUESTION_SAVE_ERROR_MESSAGE;
    return { message, issues: narrowQuestionApiIssues(record.issues) };
  }
  return { message: QUESTION_SAVE_ERROR_MESSAGE, issues: [] };
}

export function CreateQuestionModal({
  blockType,
  gated,
  isOpen,
  onClose,
  onCreated,
  initialQuestionText,
  initialTitle,
}: CreateQuestionModalProps): React.ReactElement {
  const { tokens } = useTheme();
  const createMutation = useCreateQuestionMutation();
  const { clientContext } = useClientContext();
  const bankLabel = clientContext && toScopeAccountId(clientContext.id) !== null
    ? `${getAccountLabel(clientContext)} question bank`
    : 'Question Bank';

  const initialBlock = useMemo(
    () => buildDraftBlock(blockType, gated, initialQuestionText, initialTitle),
    [blockType, gated, initialQuestionText, initialTitle],
  );
  // "Add to public question bank" defaults checked for Likert only.
  const initialIsPublic = blockType === 'likert-scale';
  // NA-406: this in-lesson modal now scopes to the active Content Context,
  // same as the standalone Question Bank panel (NA-403).
  const form = useQuestionDraft({ initialBlock, initialIsPublic, scopeToActiveContext: true });
  const { reset: resetForm } = form;

  // Fresh draft each time the modal opens (the component stays mounted while
  // closed so ModalShell can restore focus).
  useEffect(() => {
    if (isOpen) {
      resetForm(initialBlock, initialIsPublic);
    }
  }, [isOpen, initialBlock, initialIsPublic, resetForm]);

  const typeLabel = questionBlockTypeLabel(blockType);
  const isSaving = createMutation.isPending;

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = async () => {
    const draft = form.validate();
    if (!draft) return;
    try {
      const dto = await createMutation.mutateAsync({ draft });
      onCreated(dto);
      onClose();
    } catch (error) {
      const { message, issues } = toDisplayError(error);
      const issueMessages = issues.map((issue) => issue.message);
      form.setErrors([...new Set([message, ...issueMessages])]);
    }
  };

  const styles = useMemo((): Record<string, CSSProperties> => ({
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
      padding: `${tokens.spacing.lg} ${tokens.spacing.lg} 0`,
    },
    title: {
      margin: 0,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '20px',
      fontWeight: 700,
      color: tokens.text.primary,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      fontSize: '22px',
      lineHeight: 1,
      padding: 0,
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      overflowY: 'auto',
      flex: 1,
    },
    infoNotice: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.md,
      backgroundColor: tokens.banner.info.background,
      border: `1px solid ${tokens.banner.info.border}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      color: tokens.text.primary,
      lineHeight: 1.4,
    },
    infoIcon: {
      fontSize: '14px',
      color: tokens.banner.info.border,
      marginTop: '2px',
      flexShrink: 0,
    },
  }), [tokens]);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabelledBy="create-question-modal-title"
      width="760px"
      disableBackdropClose
    >
      <div style={styles.header}>
        <h2 id="create-question-modal-title" style={styles.title}>
          New {typeLabel}
        </h2>
        <button
          type="button"
          style={styles.closeButton}
          onClick={handleClose}
          aria-label="Close modal"
        >
          ×
        </button>
      </div>

      <div style={styles.body}>
        <div style={styles.infoNotice}>
          <span style={styles.infoIcon} aria-hidden="true">
            <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: 14 }} />
          </span>
          <span>{`This question will be saved to the ${bankLabel} and added to this lesson block.`}</span>
        </div>

        <QuestionForm
          block={form.block}
          onFieldChange={form.setField}
          isPublic={form.isPublic}
          onIsPublicChange={form.setIsPublic}
          errors={form.errors}
          autoFocus
          scopeToActiveContext
        />
      </div>

      <ModalFooter
        onCancel={handleClose}
        cancelDisabled={isSaving}
        actions={[
          {
            label: isSaving ? 'Saving…' : 'Save to Question Bank',
            onClick: () => { void handleSave(); },
            disabled: isSaving,
            ariaLabel: 'Save to Question Bank',
            ariaBusy: isSaving,
            variant: 'primary',
          },
        ]}
      />
    </ModalShell>
  );
}

CreateQuestionModal.displayName = 'CreateQuestionModal';
